import '../styles/search.css'
import React, { useEffect, useState, useRef, useMemo } from 'react'
import * as XLSX from 'xlsx'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import Header from '../components/Header'
import Header2 from '../components/Header2'
import PreFooter from '../components/PreFooter'
import Footer from '../components/Footer'
import SearchCard from '../components/SearchCard'
import SearchWizard from '../components/SearchWizard'
import SideResponsiveOpen from '../components/SideResponsiveOpen'
import SideBar from '../components/SideBar'
import { buildIndicatorCardKey } from '../utils/searchKeys'

import {
  getAreas,
  getTopics,
  getIndicators,
  getCountries,
  getCards,
} from '../actions/searchActions'
import { SEARCH_CARDS_RESET } from '../constants/searchConstants'


function SearchScreen() {
  const dispatch = useDispatch()
  const location = useLocation()
  const initialSearchParams = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      indicatorName: decodeURIComponent(params.get('indicator') || ''),
      indicatorId: decodeURIComponent(params.get('indicatorId') || params.get('indicator') || ''),
      topicId: decodeURIComponent(params.get('topic') || params.get('topicId') || ''),
    }
  }, [location.search])

  // Wizard keeps the last search selection for download
  const [wizardCountries, setWizardCountries] = useState([])

  const [finalList, setFinalList] = useState(null)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const [indicatorFrequencies, setIndicatorFrequencies] = useState({}) // Guardar frecuencias por tarjeta
  const indicatorFrequenciesRef = useRef({}) // Referencia para evitar problemas de closure

  const [cardPage, setCardPage] = useState(1)

  const [lastUpdateFormatted, setLastUpdateFormatted] = useState('September 2025')

  // Cargar la fecha de última actualización desde el backend (solo mes y año)
  useEffect(() => {
    const fetchLastUpdate = async () => {
      try {
        const { data } = await axios.get('/api/v1/data/last-update/')
        const raw = data.formatted || data.last_update || ''
        if (raw) {
          const d = new Date(raw)
          if (!isNaN(d)) {
            setLastUpdateFormatted(d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }))
          } else {
            // If already a formatted string like "September 26, 2025", strip the day
            const match = raw.match(/([A-Za-z]+)\s+\d+,?\s+(\d{4})/)
            if (match) setLastUpdateFormatted(`${match[1]} ${match[2]}`)
          }
        }
      } catch (error) {
        console.log('Error fetching last update:', error)
      }
    }
    fetchLastUpdate()
  }, [])

  // Mantener la referencia sincronizada con el estado
  useEffect(() => {
    indicatorFrequenciesRef.current = indicatorFrequencies;
    console.log('🔄 Referencia de frecuencias actualizada:', indicatorFrequencies);
  }, [indicatorFrequencies]);

  // Función para filtrar elementos con datos válidos (muy básica)
  const filterValidCards = (dataList) => {
    if (!dataList || !Array.isArray(dataList)) return []

    return dataList.filter(item => {
      // Verificación mínima: que el item exista y tenga las propiedades básicas
      return item &&
        item.group &&
        item.data &&
        item.group.indicator &&
        item.group.units &&
        item.group.units.length > 0
    })
  }

  useEffect(() => {
    // if (typeof window.trackPageVisit === 'function') {
    //   window.trackPageVisit('Search');
    // }

    // console.log("Using LMW-provisional database in postgresql")
    // const loadData = async () => {
    // await dispatch(resetSearch())
    dispatch({
      type: SEARCH_CARDS_RESET,
    })
    dispatch(getAreas())
    dispatch(getTopics())
    dispatch(getIndicators())
    dispatch(getCountries())
    // }
    // loadData()

  }, [dispatch])

  const userLogin = useSelector(state => state.userLogin)
  const { userInfo } = userLogin

  const topicInfo = useSelector(state => state.topicList)
  const { topicList } = topicInfo

  const areaInfo = useSelector(state => state.areaList)
  const { areaList } = areaInfo || {}

  const indicatorInfo = useSelector(state => state.indicatorList)
  const { indicatorList } = indicatorInfo

  const countryInfo = useSelector(state => state.countryList)
  const { countryList } = countryInfo





  const cardInfo = useSelector(state => state.cardList)
  const { loading: loadingCard, cardList } = cardInfo

  useEffect(() => {
    console.log("CARDLIST changed: ", cardList)
    if (cardList && cardList.data_list && Array.isArray(cardList.data_list)) {
      // Hacer copia profunda para no mutar el estado original de Redux
      const newFinalList = JSON.parse(JSON.stringify(cardList));
      console.log("Setting finalList with data_list length: ", newFinalList.data_list.length)
      setFinalList(newFinalList);

      // Inicializar frecuencias de cada indicador
      const frequencies = {};
      newFinalList.data_list.forEach(item => {
        if (item.group && item.group.indicator) {
          frequencies[buildIndicatorCardKey(item.group)] = item.group.frequency;
        }
      });
      setIndicatorFrequencies(frequencies);

    } else if (!cardList || Object.keys(cardList).length === 0) {
      // Si cardList es undefined o un objeto vacío (después de reset), limpiar finalList
      console.log("cardList is empty/undefined, clearing finalList")
      setFinalList(null)
      setIndicatorFrequencies({})
    }
  }, [cardList])



  const loadMoreHandler = () => {
    setCardPage(cardPage + 1)
  }

  const popularSearchHandler = (item) => {
    var topic = item.topic_id
    var indicator = item.indicator_id
    window.location.href = `${window.location.origin}/#/search?topic=${encodeURIComponent(topic)}&indicator=${encodeURIComponent(indicator)}`
    window.location.reload(); // Forzar recarga de la página
  }

  const downloadAllHandler = async () => {
    if (!finalList || !finalList.data_list || downloadingAll) return

    const validCards = filterValidCards(finalList.data_list).slice(0, 4 * cardPage)

    if (validCards.length === 0) return

    setDownloadingAll(true)

    console.log('🚀 Iniciando descarga de todos los indicadores');
    console.log('📊 Estado actual de frecuencias:', indicatorFrequenciesRef.current);

    try {
      // Crear un workbook con múltiples hojas
      const workbook = XLSX.utils.book_new()

      // Obtener los países seleccionados por el usuario
      const selectedCountries = wizardCountries

      // Procesar cada indicador de forma secuencial para obtener TODOS los datos
      for (let index = 0; index < validCards.length; index++) {
        const item = validCards[index]
        const { group: meta } = item

        // Obtener la frecuencia actual del estado (que se actualiza cuando el usuario cambia la frecuencia)
        // Si no existe en el estado, usar la frecuencia por defecto del meta
        // Usamos .current para obtener el valor más reciente y evitar problemas de closure
        const cardKey = buildIndicatorCardKey(meta)
        const currentFrequency = indicatorFrequenciesRef.current[cardKey] || meta.frequency

        console.log(`📥 Descargando "${meta.indicator}" con frecuencia: ${currentFrequency}`,
          `(Estado: ${indicatorFrequenciesRef.current[cardKey]}, Default: ${meta.frequency})`);
        try {
          // Hacer una solicitud individual para cada indicador con TODOS los países seleccionados
          const config = {
            headers: {
              'Content-type': 'application/json'
            }
          }

          const { data: response } = await axios.post(
            '/api/v1/data/change-card-data/',
            {
              topic: meta.topic,
              indicator: meta.indicator,
              country: selectedCountries,
              unit: '',
              frequency: currentFrequency,
            },
            config
          )

          // El backend devuelve { data_dict: [...], Message: "Success" }
          const fullData = response.data_dict || []

          console.log(`📦 Datos recibidos para ${meta.indicator}:`, fullData.length, 'registros');

          // Filtrar datos por la frecuencia actual de la tarjeta
          const filteredData = fullData.filter(dataItem =>
            dataItem.frequency.toLowerCase().trim() === currentFrequency.toLowerCase().trim()
          )

          // Si no hay datos con la frecuencia correcta, usar todos los datos
          const dataToUse = filteredData.length > 0 ? filteredData : fullData

          console.log(`Descargando ${meta.indicator}: ${dataToUse.length} registros con frecuencia ${currentFrequency}`)

          // Preparar los datos para este indicador
          // Usar 'year' de la nueva DB, con fallback a 'period' para compatibilidad
          const sortData = dataToUse.map(dataItem => ({
            indicator: meta.indicator,
            country: dataItem.country,
            frequency: dataItem.frequency,
            year: dataItem.year || dataItem.period || '',
            date: dataItem.date,
            unit: dataItem.unit,
            definition: dataItem.definition,
            source: dataItem.source,
            value: dataItem.value,
            transformation: dataItem.transformation || '',
            notes: dataItem.notes || ''
          }))

          // Crear worksheet para este indicador
          const worksheet = XLSX.utils.json_to_sheet(sortData)

          // Nombre de la hoja (limitado a 31 caracteres para Excel)
          let sheetName = meta.indicator.substring(0, 31)
          if (sheetName.length === 31) {
            sheetName = sheetName.substring(0, 28) + '...'
          }

          // Asegurar que el nombre de la hoja sea único
          let finalSheetName = sheetName
          let counter = 1
          while (workbook.SheetNames.includes(finalSheetName)) {
            const suffix = `_${counter}`
            finalSheetName = sheetName.substring(0, 31 - suffix.length) + suffix
            counter++
          }

          XLSX.utils.book_append_sheet(workbook, worksheet, finalSheetName)
        } catch (error) {
          console.error(`Error descargando datos para ${meta.indicator}:`, error)
          // Si falla, usar los datos que ya tenemos en memoria
          const { data } = item
          const sortData = data.map(dataItem => ({
            indicator: meta.indicator,
            country: dataItem.country,
            frequency: dataItem.frequency,
            year: dataItem.year || dataItem.period || '',
            date: dataItem.date,
            unit: dataItem.unit,
            definition: dataItem.definition,
            source: dataItem.source,
            value: dataItem.value,
            transformation: dataItem.transformation || '',
            notes: dataItem.notes || ''
          }))

          const worksheet = XLSX.utils.json_to_sheet(sortData)
          let sheetName = meta.indicator.substring(0, 31)
          if (sheetName.length === 31) {
            sheetName = sheetName.substring(0, 28) + '...'
          }

          let finalSheetName = sheetName
          let counter = 1
          while (workbook.SheetNames.includes(finalSheetName)) {
            const suffix = `_${counter}`
            finalSheetName = sheetName.substring(0, 31 - suffix.length) + suffix
            counter++
          }

          XLSX.utils.book_append_sheet(workbook, worksheet, finalSheetName)
        }
      }

      // Generar nombre del archivo
      const timestamp = new Date().toISOString().split('T')[0]
      const fileName = `All_Indicators_${timestamp}.xlsx`

      // Descargar el archivo
      XLSX.writeFile(workbook, fileName)
    } catch (error) {
      console.error('Error en la descarga:', error)
    } finally {
      setDownloadingAll(false)
    }
  }



  return (
    <div>
      {userInfo ? (
        <Header2 />
      ) : (
        <Header />
      )}

      <div className='search-page-body d-flex'>
        {userInfo && (
          <SideBar />
        )}
        <div style={{ flex: '1' }} className='search-main-container'>
          <div className='search-shell-intro'>
            <div className='search-shell-kicker'>Explore and compare macroeconomic series across the region</div>
          </div>

          <div className='search-grid'>
            <aside className='search-sidebar'>
              <SearchWizard
                countryList={countryList}
                indicatorList={indicatorList}
                topicList={topicList}
                areaList={areaList}
                loading={loadingCard}
                initialIndicator={initialSearchParams.indicatorName}
                initialIndicatorId={initialSearchParams.indicatorId}
                initialTopicId={initialSearchParams.topicId}
                onResetAll={() => {
                  setFinalList(null)
                  setCardPage(1)
                  setWizardCountries([])
                  setIndicatorFrequencies({})
                  dispatch({ type: SEARCH_CARDS_RESET })
                }}
                onSearch={(indicators, countries) => {
                  const requiresTopicDisambiguation = indicators.some(selectedIndicator =>
                    (indicatorList || []).filter(indicator => indicator.name === selectedIndicator.name).length > 1
                  )
                  const selectedTopics = [...new Set(
                    indicators
                      .map(indicator => (topicList || []).find(topic => topic.id === indicator.foreign_topic)?.name)
                      .filter(Boolean)
                  )]
                  setFinalList(null)
                  setCardPage(1)
                  setWizardCountries(countries)
                  dispatch({ type: SEARCH_CARDS_RESET })
                  dispatch(
                    getCards(
                      '',
                      requiresTopicDisambiguation ? selectedTopics : [],
                      indicators.map(indicator => indicator.name),
                      countries,
                      ''
                    )
                  )
                }}
              />
            </aside>

            <div className='search-cards-col'>
              {finalList && filterValidCards(finalList.data_list).length > 0 && (
                <div className='search-results-toolbar'>
                  <button onClick={downloadAllHandler} className='search-button search-button-floating' disabled={downloadingAll}>
                    {downloadingAll ? (
                      <>
                        <Spinner animation="border" as={'span'} size="sm" className='me-2' />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-download pe-2"></i>
                        Download All ({filterValidCards(finalList.data_list).slice(0, 4 * cardPage).length} indicators)
                      </>
                    )}
                  </button>
                </div>
              )}

              {loadingCard ? (
                <div className='h-100 d-flex align-items-center justify-content-center' style={{ minHeight: 300 }}>
                  <Spinner animation="border" as={'span'} style={{ width: '20px', height: '20px' }} />
                </div>
              ) : finalList && finalList.data_list ? (
                filterValidCards(finalList.data_list)
                .slice(0, 4 * cardPage)
                .map((item) => {
                  const cardKey = buildIndicatorCardKey(item.group)
                  return (
                    <SearchCard
                      key={cardKey}
                      meta={item.group}
                      data={item.data}
                      countries={wizardCountries.map(c => ({ label: c }))}
                      onFrequencyChange={(frequency) => {
                        setIndicatorFrequencies(prev => ({
                          ...prev,
                          [cardKey]: frequency
                        }))
                      }}
                    />
                  )
                })
              ) : (
                <div className='search-empty-state'>
                  <div className='search-empty-hero'>
                    <div className='search-empty-artwork'>
                      <div className='search-empty-artwork-glow'></div>
                      <img src="/static/empty-search.svg" className='search-empty-image' alt='Search workflow illustration' />
                    </div>

                    <div className='search-empty-copy'>
                      <span className='search-empty-kicker'>Search Workspace</span>
                      <h2 className='search-empty-title'>Build a regional view in three quick steps.</h2>
                      <p className='search-empty-description'>
                        Use the panel on the left to define the countries and indicators you want to compare. Once you run the search, each indicator will open as an interactive chart with download and frequency controls.
                      </p>

                      <div className='search-empty-steps'>
                        <div className='search-empty-step'>
                          <span className='search-empty-step-number'>01</span>
                          <div>
                            <div className='search-empty-step-title'>Select countries</div>
                            <div className='search-empty-step-text'>Choose one or more economies to define the comparison set.</div>
                          </div>
                        </div>

                        <div className='search-empty-step'>
                          <span className='search-empty-step-number'>02</span>
                          <div>
                            <div className='search-empty-step-title'>Pick indicators</div>
                            <div className='search-empty-step-text'>Filter by area or topic and review availability before adding a series.</div>
                          </div>
                        </div>

                        <div className='search-empty-step'>
                          <span className='search-empty-step-number'>03</span>
                          <div>
                            <div className='search-empty-step-title'>Update the visualization</div>
                            <div className='search-empty-step-text'>Run the query to generate cards, adjust frequency, and export the data you need.</div>
                          </div>
                        </div>
                      </div>

                      <div className='search-empty-tip'>
                        <span className='search-empty-tip-label'>Pro tip</span>
                        <span className='search-empty-tip-text'>Start with a small set of countries to compare frequency availability faster.</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {finalList != null && (cardPage * 4 < filterValidCards(finalList.data_list).length) && (
                <div className='d-flex justify-content-center pt-5'>
                  <button onClick={loadMoreHandler} className='search-button'>Load More</button>
                </div>
              )}
            </div>
          </div>

          <div className='search-updated-footer'>
            <div className='search-updated-footer-line'></div>
            <div className='search-updated-footer-card'>
              <span className='search-updated-footer-label'>Dataset Updated</span>
              <span className='search-updated-footer-value'>{lastUpdateFormatted}</span>
            </div>
          </div>

          <br /><br /><br />

        </div>

      </div>

      {userInfo && <SideResponsiveOpen />}
      <PreFooter />
      <Footer />
    </div>
  )
}

export default SearchScreen
